import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import BrokerDashboard from "./pages/broker/Dashboard";
import PropertyUpload from "./pages/broker/PropertyUpload";
import PropertyEdit from "./pages/broker/PropertyEdit";
import BrokerLogin from "./pages/broker/Login";
import BrokerSignup from "./pages/broker/Signup";
import BrokerProperties from "./pages/broker/Properties";
import BrokerLeads from "./pages/broker/Leads";
import AdminDashboard from "./pages/admin/Dashboard";
import PropertyManagement from "./pages/admin/PropertyManagement";
import AdminPropertyUpload from "./pages/admin/PropertyUpload";
import AdminPropertyEdit from "./pages/admin/PropertyEdit";
import MarketInsights from "./pages/admin/MarketInsights";
import MarketInsightsPublic from "./pages/MarketInsights";
import MarketInsightCreate from "./pages/admin/MarketInsightCreate";
import MarketInsightEdit from "./pages/admin/MarketInsightEdit";
import Brokers from "./pages/admin/Brokers";
import BrokerProfile from "./pages/admin/BrokerProfile";
import Leads from "./pages/admin/Leads";
import BlogManagement from "./pages/admin/Blog";
import BlogCreate from "./pages/admin/BlogCreate";

import BlogEdit from "./pages/admin/BlogEdit";
import NotFound from "./pages/NotFound";
import LocationDetail from "./pages/LocationDetail";
import Contact from "./pages/Contact";
import MortgageCalculator from "./pages/MortgageCalculator";
import Developers from "./pages/Developers";
import Locations from "./pages/Locations";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import DeveloperDetails from "./pages/DeveloperDetails";
import AdminLogin from "./pages/admin/Login";
import MainLayout from "./components/layout/MainLayout";
import AuthLayout from "./components/layout/AuthLayout";
import { AuthProvider } from "./context/AuthContext";
import MarketInsightDetail from "./pages/MarketInsightDetail";
import ScrollToTop from "./components/layout/ScrollToTop";
import ContactModal from "./components/ContactModal";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <ContactModal />
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-blue-900/20">
              <Routes>

                {/* Public routes */}
                <Route element={<MainLayout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/properties" element={<Properties />} />
                  <Route path="/properties/:id" element={<PropertyDetail />} />
                  <Route path="/locations/:id" element={<LocationDetail />} />
                  <Route path="/locations" element={<Locations />} />
                  <Route path="/developers" element={<Developers />} />
                  <Route path="/developers/:developer" element={<DeveloperDetails />} />
                  <Route path="/market-insights" element={<MarketInsightsPublic />} />
                  <Route path="/market-insights/:slug" element={<MarketInsightDetail />} />
                  <Route path="/blogs" element={<Blog />} />
                  <Route path="/blogs/:slug" element={<BlogDetail />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/mortgage-calculator" element={<MortgageCalculator />} />
                  <Route path="*" element={<NotFound />} />
                </Route>

                {/* Broker routes */}
                <Route path="/broker/login" element={
                  <AuthLayout authentication={false}>
                    <BrokerLogin />
                  </AuthLayout>
                } />
                <Route path="/broker/signup" element={
                  <AuthLayout authentication={false}>
                    <BrokerSignup />
                  </AuthLayout>
                } />
                <Route path="/broker/dashboard" element={
                  <AuthLayout authentication roles={["BROKER"]}>
                    <BrokerDashboard />
                  </AuthLayout>
                } />
                <Route path="/broker/property-upload" element={
                  <AuthLayout authentication roles={["BROKER"]}>
                    <PropertyUpload />
                  </AuthLayout>
                } />
                <Route path="/broker/properties" element={
                  <AuthLayout authentication roles={["BROKER"]}>
                    <BrokerProperties />
                  </AuthLayout>
                } />
                <Route path="/broker/property-edit/:id" element={
                  <AuthLayout authentication roles={["BROKER"]}>
                    <PropertyEdit />
                  </AuthLayout>
                } />
                <Route path="/broker/leads" element={
                  <AuthLayout authentication roles={["BROKER"]}>
                    <BrokerLeads />
                  </AuthLayout>
                } />

                {/* Admin routes */}
                <Route path="/admin/login" element={
                  <AuthLayout authentication={false}>
                    <AdminLogin />
                  </AuthLayout>
                } />
                <Route path="/admin/dashboard" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <AdminDashboard />
                  </AuthLayout>
                } />
                <Route path="/admin/property-management" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <PropertyManagement />
                  </AuthLayout>
                } />
                <Route path="/admin/property-upload" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <AdminPropertyUpload />
                  </AuthLayout>
                } />
                <Route path="/admin/property-edit/:id" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <AdminPropertyEdit />
                  </AuthLayout>
                } />
                <Route path="/admin/market-insights" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <MarketInsights />
                  </AuthLayout>
                } />
                <Route path="/admin/market-insights/create" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <MarketInsightCreate />
                  </AuthLayout>
                } />
                <Route path="/admin/market-insights/edit/:slug" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <MarketInsightEdit />
                  </AuthLayout>
                } />
                <Route path="/admin/brokers" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <Brokers />
                  </AuthLayout>
                } />
                <Route path="/admin/broker-profile/:id" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <BrokerProfile />
                  </AuthLayout>
                } />
                <Route path="/admin/leads" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <Leads />
                  </AuthLayout>
                } />
                <Route path="/admin/blog" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <BlogManagement />
                  </AuthLayout>
                } />
                <Route path="/admin/blog/create" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <BlogCreate />
                  </AuthLayout>
                } />
                <Route path="/admin/blog/edit/:slug" element={
                  <AuthLayout authentication roles={["ADMIN"]}>
                    <BlogEdit />
                  </AuthLayout>
                } />



              </Routes>
            </div>
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
